/**
 * BigSplice Chat Input Component
 * Input field with command autocomplete suggestions and file selector
 */

import React, { useState, useEffect, useRef, KeyboardEvent } from 'react';
import { lambdaService, FileInfo } from '../../services/lambdaService';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Send,
  Folder,
  BarChart2,
  Box,
  Upload,
  Settings,
  Terminal,
  FileText,
  ChevronDown,
  X,
  Loader2,
} from 'lucide-react';
import { cn } from '../../lib/utils';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  isLoading: boolean;
  selectedFile?: string | null;
  selectedFileKey?: string | null;
  onSelectFile?: (name: string | null, key: string | null) => void;
}

type CommandSuggestion = {
  command: string;
  description: string;
  example: string;
  category: 'files' | 'analysis' | 'visualization' | 'upload' | 'settings';
};

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isLoading,
  selectedFile,
  selectedFileKey,
  onSelectFile,
}) => {
  const [input, setInput] = useState('');
  const [suggestions, setSuggestions] = useState<CommandSuggestion[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFileSelector, setShowFileSelector] = useState(false);
  const [files, setFiles] = useState<FileInfo[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileSelectorRef = useRef<HTMLDivElement>(null);

  // Load files when file selector is opened
  useEffect(() => {
    if (showFileSelector && files.length === 0) {
      setFilesLoading(true);
      lambdaService.listFiles().then((result) => {
        if (result.success && result.files) {
          setFiles(result.files);
        }
        setFilesLoading(false);
      });
    }
  }, [showFileSelector, files.length]);

  // Close file selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (fileSelectorRef.current && !fileSelectorRef.current.contains(event.target as Node)) {
        setShowFileSelector(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleFileSelect = (file: FileInfo) => {
    if (onSelectFile) {
      onSelectFile(file.fileName, file.s3Key || null);
    }
    setShowFileSelector(false);
  };

  const handleClearFile = () => {
    if (onSelectFile) {
      onSelectFile(null, null);
    }
  };

  // Update suggestions as user types
  useEffect(() => {
    if (input.length > 0) {
      const newSuggestions = lambdaService.getChatCommandSuggestions(input);
      setSuggestions(newSuggestions);
      setShowSuggestions(newSuggestions.length > 0);
      setSelectedIndex(0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [input]);

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'files':
        return <Folder className="h-3 w-3" />;
      case 'analysis':
        return <BarChart2 className="h-3 w-3" />;
      case 'visualization':
        return <Box className="h-3 w-3" />;
      case 'upload':
        return <Upload className="h-3 w-3" />;
      case 'settings':
        return <Settings className="h-3 w-3" />;
      default:
        return <Terminal className="h-3 w-3" />;
    }
  };

  const handleSend = () => {
    if (!input.trim() || isLoading) return;
    onSendMessage(input.trim());
    setInput('');
    setShowSuggestions(false);
  };

  const handleSelectSuggestion = (suggestion: CommandSuggestion) => {
    setInput(suggestion.command);
    setShowSuggestions(false);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (showSuggestions && suggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < suggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : suggestions.length - 1
          );
          break;
        case 'Tab':
        case 'Enter':
          if (e.key === 'Tab' || (e.key === 'Enter' && !e.shiftKey)) {
            e.preventDefault();
            handleSelectSuggestion(suggestions[selectedIndex]);
          }
          break;
        case 'Escape':
          setShowSuggestions(false);
          break;
      }
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(
        textareaRef.current.scrollHeight,
        200
      )}px`;
    }
  }, [input]);

  return (
    <div className="border-t bg-background p-4">
      <div className="max-w-4xl mx-auto relative">
        {/* Suggestions Panel */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-popover border rounded-lg shadow-lg overflow-hidden z-10">
            <div className="p-1">
              {suggestions.map((suggestion, index) => (
                <div
                  key={suggestion.command}
                  className={cn(
                    'flex items-start gap-3 p-2 rounded-md cursor-pointer',
                    index === selectedIndex
                      ? 'bg-accent'
                      : 'hover:bg-accent/50'
                  )}
                  onClick={() => handleSelectSuggestion(suggestion)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div
                    className={cn(
                      'mt-0.5 p-1 rounded',
                      index === selectedIndex
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted'
                    )}
                  >
                    {getCategoryIcon(suggestion.category)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium">
                      {suggestion.command}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {suggestion.description}
                    </div>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {suggestion.category}
                  </div>
                </div>
              ))}
            </div>
            <div className="px-3 py-1.5 border-t bg-muted/50 text-xs text-muted-foreground">
              <span className="font-medium">Tab</span> to select |{' '}
              <span className="font-medium">Enter</span> to send |{' '}
              <span className="font-medium">Esc</span> to close
            </div>
          </div>
        )}

        {/* File Selector */}
        {onSelectFile && (
          <div className="mb-2 relative" ref={fileSelectorRef}>
            {selectedFile ? (
              <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 rounded-lg px-3 py-2">
                <FileText className="h-4 w-4 text-primary flex-shrink-0" />
                <span className="text-sm font-medium truncate flex-1">{selectedFile}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 flex-shrink-0"
                  onClick={handleClearFile}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setShowFileSelector(!showFileSelector)}
              >
                <FileText className="h-4 w-4" />
                Select a file to analyze
                <ChevronDown className={cn("h-4 w-4 transition-transform", showFileSelector && "rotate-180")} />
              </Button>
            )}

            {/* File Dropdown */}
            {showFileSelector && (
              <div className="absolute bottom-full left-0 mb-2 w-80 max-h-64 overflow-y-auto bg-popover border rounded-lg shadow-lg z-20">
                {filesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : files.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground text-center">
                    No files found. Upload a file first.
                  </div>
                ) : (
                  <div className="p-1">
                    {files.map((file) => (
                      <div
                        key={file.s3Key}
                        className="flex items-center gap-2 p-2 rounded-md cursor-pointer hover:bg-accent"
                        onClick={() => handleFileSelect(file)}
                      >
                        <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.fileName}</p>
                          <p className="text-xs text-muted-foreground">
                            {file.fileSize ? `${(file.fileSize / 1024 / 1024).toFixed(1)} MB` : 'Unknown size'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Input Area */}
        <div className="flex items-end gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (input.length > 0) {
                setShowSuggestions(suggestions.length > 0);
              }
            }}
            onBlur={() => {
              // Delay to allow click on suggestion
              setTimeout(() => setShowSuggestions(false), 200);
            }}
            placeholder={selectedFile ? `Ask about ${selectedFile}...` : "Select a file above or type a command..."}
            className="min-h-[44px] max-h-[200px] resize-none"
            rows={1}
            disabled={isLoading}
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            size="icon"
            className="h-11 w-11 flex-shrink-0"
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>

        {/* Quick tip */}
        <div className="mt-2 text-xs text-muted-foreground text-center">
          {selectedFile ? (
            <>Ask questions about your data or try commands like <code className="bg-muted px-1 py-0.5 rounded">show columns</code></>
          ) : (
            <>Select a file above, or type <code className="bg-muted px-1 py-0.5 rounded">list files</code> to see available files</>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
